import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BeneficiaryContribution } from "@/types/contributions";
import { AllocationControls } from "./AllocationControls";
import { AllocationRow } from "./AllocationRow";
import Tooltip from "@/components/Common/Tooltip";

export const BeneficiaryContributionAllocation = ({
  type,
  label,
  total,
  beneficiaries,
  toolTipText,
  onUpdate,
  onSplit,
  onReset,
}: {
  type: "personal" | "employer";
  label: string;
  total: number;
  toolTipText: string;
  beneficiaries: BeneficiaryContribution[];
  onUpdate: (id: number, newPercentage: number) => void;
  onSplit: () => void;
  onReset: () => void;
}) => {
  const { t } = useTranslation();
  //const [isExpanded, setIsExpanded] = useState(beneficiaries.length === 1);
  const [isExpanded, setIsExpanded] = useState(true);

  // Update expanded state when beneficiaries change
  /*   useEffect(() => {
    if (beneficiaries.length === 1) {
      setIsExpanded(true);
    }
  }, [beneficiaries.length]); */

  // Calculate remaining amount and percentage
  const totalAllocated = beneficiaries.reduce((sum, beneficiary) => {
    return (
      sum +
      (type === "personal"
        ? beneficiary.employeeContributionInDollars
        : beneficiary.employerContributionInDollars)
    );
  }, 0);

  const remainingAmount = Math.max(0, total - totalAllocated);
  const remainingPercentage =
    total > 0 ? Math.max(0, (remainingAmount / total) * 100) : 0;

  const hideSplitButton = beneficiaries.length === 1;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <div className="text-[18px] shadow-introBox font-outfit bg-white w-full rounded-[16px]">
        <div
          className={`flex justify-between px-[16px] py-[32px] md:p-[32px] cursor-pointer hover:bg-gray-50 transition-colors duration-200 ${
            isExpanded ? "rounded-t-[16px]" : "rounded-[16px]"
          }`}
          onClick={toggleExpanded}
        >
          <div className="flex flex-col space-y-[5px]">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <p className="text-[16px] font-normal text-[#8B8B8B] leading-[127%]">
                  {label}
                  <span className="text-[18px] ml-1">
                    <Tooltip text={toolTipText} />
                  </span>
                </p>
                {/* Chevron Icon */}
                <svg
                  className={`w-4 h-4 text-[#8B8B8B] transition-transform duration-200 ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {beneficiaries.length > 1 && (
              <div className="text-[18px] font-normal text-black leading-[127%]">
                {remainingPercentage.toFixed(0)}% (${remainingAmount.toFixed(2)}
                )
              </div>
            )}
          </div>
          {isExpanded && (
            <div onClick={(e) => e.stopPropagation()}>
              <AllocationControls
                onSplit={onSplit}
                onReset={onReset}
                hideSplitButton={hideSplitButton}
                splitTooltipText={t(
                  "onboarding.contributions.allocation_tooltips.split_equal"
                )}
                resetTooltipText={t(
                  "onboarding.contributions.allocation_tooltips.reset"
                )}
              />
            </div>
          )}
        </div>

        {isExpanded && (
          <div className="border-t border-gray-100">
            {beneficiaries.map((beneficiary) => (
              <AllocationRow
                key={`${type}-${beneficiary.beneficiaryId}`}
                beneficiary={beneficiary}
                type={type}
                fundTotal={total}
                beneficiaries={beneficiaries}
                onUpdate={(beneficiaryId, newPercentage) =>
                  onUpdate(beneficiaryId, newPercentage)
                }
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};
